import { useMutation } from "@tanstack/react-query";
import { useCallback, useContext, useState } from "react";
import { useNavigate } from "react-router";

import { routes } from "~/app/routes/web-routes.ts";
import type { SendDocsBody } from "~/shared/schemas/send-docs-schema.ts";
import { sendDocsResponseSchema } from "~/shared/schemas/send-docs-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";
import { scrollMainContentToTop } from "~/shared/utils/scroll-to-top.ts";

import { QuoteDetailsContext } from "../context/quote-details-context.tsx";
import {
	useLoadingState,
	useModalManager,
	useQuoteLoader,
} from "./use-quote-details-hooks.ts";

export { useQuoteDetailsPageLoadQueries } from "./use-quote-details-hooks.ts";

// Modal states specific to shipment-details component
type ShipmentDetailsModalState = {
	confirmation: boolean;
	tender: boolean;
	error: boolean;
};

export const useShipmentDetailsModal = () => {
	return useModalManager<ShipmentDetailsModalState>({
		confirmation: false,
		tender: false,
		error: false,
	});
};

export const useShipmentDetailsLoader = (options?: {
	includeFormReset?: boolean;
	includeCustomerData?: boolean;
}) => {
	return useQuoteLoader({
		includeFormReset: options?.includeFormReset ?? false,
		includeCustomerData: options?.includeCustomerData ?? false,
	});
};

export const useShipmentDetailsSaving = () => {
	return useLoadingState(false);
};

export const useShipmentDetailsHandlers = () => {
	const modalManager = useShipmentDetailsModal();
	const savingState = useShipmentDetailsSaving();
	const { resetForm } = useContext(QuoteDetailsContext);
	const [errorMessage, setErrorMessage] = useState("");
	const navigate = useNavigate();

	const handleIsSaving = useCallback(
		(isSaving: boolean) => {
			savingState.setIsLoading(isSaving);
		},
		[savingState],
	);

	const handleShowConfirmationModal = useCallback(
		(shouldShow?: boolean) => {
			modalManager.toggleModal("confirmation", shouldShow);
		},
		[modalManager],
	);

	const handleShowTenderModal = useCallback(
		(shouldShow?: boolean) => {
			const isCurrentlyOpen = modalManager.showModal.tender;
			const willBeClosed =
				shouldShow === false || (shouldShow === undefined && isCurrentlyOpen);

			modalManager.toggleModal("tender", shouldShow);

			// Navigate to new quote form when modal is closed
			if (willBeClosed) {
				resetForm();
				setTimeout(() => {
					navigate(routes.quoteDetails);
					// Reset scroll to the top of the page so the new quote form
					// starts at the top instead of inheriting the previous page's
					// scroll position (e.g. the bottom of the shipment details page).
					scrollMainContentToTop();
				}, 500);
			}
		},
		[modalManager, navigate, resetForm],
	);

	const handleShowErrorModal = useCallback(
		(shouldShow: boolean, message = "") => {
			setErrorMessage(message);
			modalManager.toggleModal("error", shouldShow);
		},
		[setErrorMessage, modalManager],
	);

	return {
		...modalManager,
		...savingState,
		handleIsSaving,
		handleShowConfirmationModal,
		handleShowTenderModal,
		handleShowErrorModal,
		isSaving: savingState.isLoading,
		errorMessage,
	};
};

export const useSendDocs = () => {
	return useMutation({
		mutationFn: async (body: SendDocsBody) => {
			return await postAndValidate(
				apiRoutes.SENDDOCS,
				body,
				sendDocsResponseSchema,
			);
		},
	});
};
